import React, { useMemo, useState } from 'react';
import { Plus, ShieldCheck, Trash2, SquarePen, ArrowUpDown, Search } from 'lucide-react';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useRolesList, useDeleteRole } from '../../hooks/useRoles';
import type { Role } from '../../types/role';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { RoleEditorModal } from './components/RoleEditorModal';
import { useSort } from '../../hooks/useSort';
import { Modal } from '../../components/common/Modal';

export const RolesPage: React.FC = () => {
  const { t } = useLanguage();
  const { canEditRoles } = usePermissions();
  const { data: rawRoles, isLoading, refetch } = useRolesList();
  const roles = useMemo(
    () => (Array.isArray(rawRoles) ? rawRoles : []),
    [rawRoles]
  );

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMut = useDeleteRole();

  // Hide super_admin from company admins — it's a platform-level entity, not
  // meaningful here. Catches both the canonical 'super_admin' and the legacy
  // 'super admin' (space) variant from migration 080, regardless of company
  // scope, since per-company copies are also redundant artifacts.
  const visibleRoles = useMemo(
    () => roles.filter((r) => r.name !== 'super_admin' && r.name !== 'super admin'),
    [roles],
  );
  const systemRoles = useMemo(() => visibleRoles.filter((r) => r.is_system), [visibleRoles]);
  const customRoles = useMemo(() => visibleRoles.filter((r) => !r.is_system), [visibleRoles]);

  const { sortedItems, sortConfig, handleSort } = useSort<Role>(customRoles, {
    key: 'member_count' as keyof Role,
    direction: 'desc',
  });

  const openCreate = () => {
    if (!canEditRoles) return;
    setEditingId(null);
    setEditorOpen(true);
  };

  const openEdit = (id: string) => {
    setEditingId(id);
    setEditorOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      const code = (err as { response?: { data?: { error?: string; count?: number } } })?.response?.data;
      if (code?.error === 'role.has_members') {
        setDeleteError(
          t('roles.delete.hasMembers', { count: code.count ?? 0 }, `Reassign ${code.count ?? 0} members first`),
        );
      } else {
        setDeleteError(t('roles.delete.failed', undefined, 'Failed to delete role'));
      }
    }
  };

  if (isLoading) return <LoadingSpinner text={t('common.loading', undefined, 'Loading…')} />;

  const headerCell = (key: keyof Role, labelKey: string, fallback: string) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(key)}
    >
      <div className="flex items-center gap-2">
        {t(labelKey, undefined, fallback)}
        {sortConfig?.key === key && <ArrowUpDown className="h-4 w-4" />}
      </div>
    </th>
  );

  const renderRow = (role: Role) => (
    <tr key={role.id} data-test-id={`role-row-${role.id}`} className="hover:bg-gray-50">
      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {role.is_system && (
            <ShieldCheck
              className="h-4 w-4 text-gray-400"
              aria-label={t('roles.badges.system', undefined, 'System role')}
            />
          )}
          <span>{role.display_name}</span>
          {!role.is_system && (role.name === 'staff' || role.name === 'viewer') && (
            <span className="ml-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
              {t('roles.badges.default', undefined, 'Default')}
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-500 font-mono whitespace-nowrap">{role.name}</td>
      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">{role.member_count ?? 0}</td>
      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{new Date(role.created_at).toLocaleDateString()}</td>
      <td className="px-6 py-4 text-right text-sm font-medium space-x-2 whitespace-nowrap">
        <button
          type="button"
          data-test-id={`role-edit-${role.id}`}
          onClick={() => openEdit(role.id)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100"
        >
          {role.is_system || !canEditRoles ? <Search className="w-4 h-4" /> : <SquarePen className="w-4 h-4" />}
          {role.is_system || !canEditRoles
            ? t('common.view', undefined, 'View')
            : t('common.edit', undefined, 'Edit')}
        </button>
        {!role.is_system && canEditRoles && (
          <button
            type="button"
            data-test-id={`role-delete-${role.id}`}
            disabled={(role.member_count ?? 0) > 0}
            onClick={() => setDeleteTarget(role)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-red-700 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              (role.member_count ?? 0) > 0
                ? t('roles.delete.reassignFirst', undefined, 'Reassign members first')
                : undefined
            }
          >
            <Trash2 className="w-4 h-4" />
            {t('common.delete', undefined, 'Delete')}
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-6" data-test-id="roles-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('roles.page.title', undefined, 'Roles & Permissions')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('roles.page.subtitle', undefined, 'Manage which actions each role can perform.')}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          disabled={!canEditRoles}
          data-test-id="roles-new-button"
          title={!canEditRoles ? t('roles.page.upgradeTooltip', undefined, 'Upgrade to customize roles') : undefined}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          {t('roles.page.newRole', undefined, 'New Role')}
        </button>
      </div>

      <div className="bg-white shadow sm:rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-[720px] w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headerCell('display_name', 'roles.table.displayName', 'Role')}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('roles.table.canonicalName', undefined, 'Canonical Name')}
              </th>
              {headerCell('member_count', 'roles.table.memberCount', 'Members')}
              {headerCell('created_at', 'roles.table.createdAt', 'Created')}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                {t('roles.page.actions', undefined, 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {systemRoles.length > 0 && (
              <tr className="bg-gray-50/60">
                <td colSpan={5} className="px-6 py-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      {t('roles.page.sectionSystem', undefined, 'Built-in Roles')}
                    </span>
                    <span className="text-xs text-gray-400">
                      · {t('roles.page.sectionSystemHint', undefined, 'Preset roles with full access.')}
                    </span>
                  </div>
                </td>
              </tr>
            )}
            {systemRoles.map(renderRow)}
            {sortedItems.length > 0 && (
              <tr className="bg-gray-50/60">
                <td colSpan={5} className="px-6 py-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      {t('roles.page.sectionCustom', undefined, 'Custom Roles')}
                    </span>
                    <span className="text-xs text-gray-400">
                      · {t('roles.page.sectionCustomHint', undefined, 'Roles tailored to your team.')}
                    </span>
                  </div>
                </td>
              </tr>
            )}
            {sortedItems.map(renderRow)}
            {roles.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  {t('roles.page.empty', undefined, 'No roles yet — create one to get started.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RoleEditorModal
        isOpen={editorOpen}
        roleId={editingId}
        onClose={() => setEditorOpen(false)}
        onSaved={() => refetch()}
      />

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        title={t('roles.delete.title', undefined, 'Delete role?')}
        maxWidth="md"
      >
        <p className="text-sm text-gray-600">
          {t('roles.delete.confirmPrefix', undefined, 'You are about to delete')}{' '}
          <span className="font-medium text-gray-900">{deleteTarget?.display_name}</span>.
        </p>
        {deleteError && <p className="mt-3 text-sm text-red-600">{deleteError}</p>}
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('common.cancel', undefined, 'Cancel')}
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleteMut.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {t('common.delete', undefined, 'Delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
};
